import React, { useState } from "react";
import {
  ArrowDown,
  BlueBirdTheme, Breadcrumb, Button, Divider, Loading, Typography,
} from "@kitbird/core";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { CardContent, PageCard, PageWrapper } from "../../../../../components/Content/Content.style";
import { HeaderText } from "../../../../../components/FloatHeader/FloatHeader.style";
import logo from "../../../../../assets/icons/logogram_bb.png";
import { ReactComponent as DownloadIcon } from "../../../../../assets/icons/download_icon.svg";
import { ReactComponent as FilterIcon } from "../../../../../assets/icons/custom_filter.svg";
import SearchInput from "../../../../../components/SearchInput";
import { useWithdrawalReceipt } from "../../hooks/Provider";
import Status, { StatusEnums } from "../../../../../components/Status";
import { toRupiah, toStringDate } from "../../../../../utils/common";
import Table from "../../../../../components/Table";
import columns from "./columns";
import { ActionSection } from "../../../../../components/Filter/Filter.style";
import emptyTable from "../../../../../assets/images/empty_table.png";
import { StickyTable, TableMessage } from "../../../../../components/Table/Table.style";
import { IGetWithdrawalData } from "../../../../../services/trxWithdrawalService";

const {
  blueGray, gray, primary,
} = BlueBirdTheme.colors;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: fit-content;
`;

const RowBox = styled.div`
  display: flex;
  gap: 9px;
  justify-content: space-between;
  cursor: pointer;
`;

const FlexBox = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-end;
`;

const RotateWrapper = styled.div<{ $isRotate: boolean }>`
  transform: ${({ $isRotate }) => ($isRotate ? "scaleY(-1)" : "scaleY(1)")};
  transition: all .3s;
`;

const WithdrawalReceiptHistory = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHistory = pathname === "/transaksi/pencairan-bon/riwayat";
  const {
    historyObsRef,
    historyTableRef,
    historyMeta,
    setModalConfig,
    historyParams,
    setHistoryParams,
    setTempHistoryParams,
    historyParamsCount,
    searchHistory,
    setSearchHistory,
    expandedHistoryId,
    setExpandedHistoryId,
    withdrawalHistoryList,
    handleDownloadXls,
    historyQuery,
  } = useWithdrawalReceipt();

  const [sort, setSort] = useState<{
    sortBy: keyof IGetWithdrawalData,
    sortType: "DEFAULT" | "ASC" | "DSC",
  }>({
    sortBy: "trx_date",
    sortType: "DEFAULT",
  });

  const {
    isFetching,
    isFetchingNextPage,
  } = historyQuery;

  const formatDateString = (dateStr: string) => {
    if (typeof dateStr !== "string" || dateStr.length !== 8) return undefined;
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${year}-${month}-${day} 00:00:00`;
  };

  const filtersComponent = <ActionSection style={{ justifyContent: "flex-start" }}>
    <SearchInput
      placeholder="ID pencairan"
      onChange={(e) => setSearchHistory(e.target.value)}
      value={searchHistory}
    />
    <Button
      variant="tertiary"
      leftIcon={<FilterIcon />}
      rightIcon={<ArrowDown />}
      textColor={historyParamsCount ? primary[500] : blueGray.light}
      backgroundColor={historyParamsCount ? primary[200] : primary[100]}
      onClick={() => setModalConfig({ type: "FILTER" })}
    >Filter
      {historyParamsCount
        ? <>&nbsp;&nbsp;&nbsp;<Status
          content={historyParamsCount}
          textColorAsBgColor
          style={{ boxSizing: "border-box", padding: "0 6px" }}
        /></>
        : ""
      }
    </Button>
  </ActionSection>;

  const historyRows = () => withdrawalHistoryList.map((item, i) => {
    const isExpanded = item.withdrawal_number === expandedHistoryId;
    return (
      <RowWrapper key={i}>
        <RowBox
          onClick={() => setExpandedHistoryId((curr) => (curr === item.withdrawal_number ? "" : item.withdrawal_number))}
        >
          <div style={{ width: "calc(100% - 48px - 16px)" }}>
            <Typography level={7} color={gray.subtitle} fontWeight="medium">
              ID: {item.withdrawal_number} •&nbsp;
            </Typography>
            <Typography level={7} color={gray.subtitle}>
              {toStringDate(formatDateString(item.value_date))}
            </Typography>
            <br />
            <Typography level={5} color={primary[500]} fontWeight="bold">
              Jumlah pencairan : {toRupiah(item.withdrawal_amount)}
            </Typography>
            <br />
            <Typography level={7} color={gray.subtitle} fontWeight="regular">Total bon : </Typography>
            <Typography level={7} color={gray.subtitle} fontWeight="bold">
              {toRupiah(item.total_amount_bon)}
            </Typography>
            <br />
          </div>
          <FlexBox>
            <div style={{ display: "flex", gap: 8 }}>
              <Status
                status={StatusEnums.WARNING}
                style={{ textWrap: "nowrap" }}
                content={<Typography level={7} fontWeight="medium">
                  Cash on hand sebelumnya : <b>
                    {toRupiah(item.cash_on_hand_before_withdrawal)}
                  </b>
                </Typography>}
              />
              <Status
                style={{ textWrap: "nowrap" }}
                content={<Typography level={7} fontWeight="medium">
                  Cash on hand : <b>
                    {toRupiah(item.cash_on_hand_balance)}
                  </b>
                </Typography>}
              />
            </div>
            <RotateWrapper $isRotate={isExpanded}>
              <ArrowDown />
            </RotateWrapper>
          </FlexBox>
        </RowBox>
        <div>
          <Typography level={7} color={gray.subtitle} fontWeight="bold">Keterangan</Typography>
          <br />
          <Typography level={7} color={gray.bodyText} fontWeight="regular">{item.trx_desc}</Typography>
        </div>
        {isExpanded
          && <>
            <StickyTable>
              <Table
                columns={columns()}
                dataSource={sort.sortBy
                  ? item.withdrawal_details.sort((a, b) => {
                    let sortValue = 0;
                    if (a[sort.sortBy] < b[sort.sortBy]) sortValue = -1;
                    if (a[sort.sortBy] > b[sort.sortBy]) sortValue = 1;
                    return sort.sortType === "DSC" ? sortValue * -1 : sortValue;
                  })
                  : item.withdrawal_details}
                sortConfig={{
                  sortBy: sort.sortBy,
                  sortType: sort.sortType,
                  onSort: (sortBy, sortType) => setSort(
                    { sortBy: sortBy as keyof IGetWithdrawalData, sortType },
                  ),
                }}
              />
            </StickyTable>
          </>
        }
        <Divider color={primary[200]} />
        <div />
      </RowWrapper>
    );
  });

  return (<>
    <PageWrapper style={{ display: isHistory ? "flex" : "none" }} >
      <HeaderText>
        <Typography level={3} color={gray.title} fontWeight="medium">Riwayat pencairan bon</Typography>
      </HeaderText>
      <Breadcrumb
        items={[
          { title: "Pencairan bon", path: "/transaksi/pencairan-bon" },
          { title: "Riwayat pencairan bon" },
        ]}
        onClick={(item) => {
          if (item.path) {
            setHistoryParams({});
            setTempHistoryParams({});
            navigate(item.path);
          }
        }}
      />

      <PageCard style={{ height: "calc(90vh - 32px - 24px - 12px)" }}>
        <CardContent style={{ gap: 16, padding: 16 }}>
          <div style={{ display: "flex", gap: 9, fontWeight: 500 }}>
            <img src={logo} width={32} height={26} />
            <Typography level={4} fontWeight="medium" color={gray.subtitle}>
              Daftar riwayat pencairan bon
            </Typography>
          </div>
          <ActionSection style={{
            alignItems: "flex-start",
            flexWrap: "unset",
          }}>
            {filtersComponent}
            <Button
              variant="secondary"
              leftIcon={<DownloadIcon />}
              style={{ minWidth: "fit-content" }}
              onClick={handleDownloadXls}
              disabled={!(
                historyParams.codeType?.value
                && historyParams.areaId
                && historyParams.pool?.value
                && historyParams.company?.value
                && historyParams.date
              )}
            >Download XLS</Button>
          </ActionSection>
          {(isFetching && !isFetchingNextPage)
            ? <Loading variant="line" />
            : <div
              ref={historyTableRef}
              style={{
                display: "grid",
                gap: 16,
                overflowY: "auto",
                height: "100%",
              }}
            >
              <StickyTable >
                {historyRows()}
              </StickyTable>
              {withdrawalHistoryList.length === 0 && (
                <TableMessage>
                  <img src={emptyTable} />
                  <Typography level={4} fontWeight="medium">
                    Maaf, Data yang anda cari belum tersedia
                  </Typography>
                </TableMessage>
              )}
              <>
                {(!isFetching && historyMeta.page < historyMeta.total_pages) && <tfoot ref={historyObsRef} style={{ height: 5, width: "100%" }} />}
                {isFetchingNextPage && <Loading variant="circular" />}
              </>
            </div>
          }
        </CardContent>
      </PageCard>
    </PageWrapper>
  </>);
};

export default WithdrawalReceiptHistory;
